import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Upload,
  Button,
  Row,
  Col,
  Image,
  Modal,
  message,
  Progress,
  Typography,
  Empty,
  Spin,
  Input,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Select,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  SearchOutlined,
  InboxOutlined,
  FileTextOutlined,
  FileOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  CarOutlined,
  PictureOutlined,
  UserOutlined,
  SkinOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload';
import { useAuth } from '../../providers/authProvider';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Search } = Input;
const { Option } = Select;

interface MediaFile {
  uid: string;
  name: string;
  size: number;
  uploadDate: string;
  url: string;
  type: string;
  isGenerating?: boolean;
  generationId?: string;
  prompt?: string;
  promptId?: string; // ComfyUI Prompt-ID für Debugging
  progress?: {
    current_step: string;
    progress_percentage: number;
    current_step_number: number;
    total_steps: number;
    queue_position?: number;
    estimated_time?: number;
  };
}

export const MediaManager: React.FC = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  
  // AI Image Generation States
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingCount, setGeneratingCount] = useState(0);
  const [testingConnection, setTestingConnection] = useState(false);
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const generationQueueRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [selectedWorkflow, setSelectedWorkflow] = useState('standard');
  const [carImage, setCarImage] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [clothingImage, setClothingImage] = useState<string | null>(null);
  const [modelImage, setModelImage] = useState<string | null>(null);
  const [roomObjectImage, setRoomObjectImage] = useState<string | null>(null);
  const [styleImage, setStyleImage] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<string | null>(null);
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [uploadCount, setUploadCount] = useState(0);
  const maxFiles = 100;

  const API_BASE_URL = '/rest/api';

  // Prompt-Historie laden
  const loadPromptHistory = async () => {
    try {
      // Lokale Historie aus localStorage
      const localHistory = localStorage.getItem('ai_prompt_history');
      const localPrompts = localHistory ? JSON.parse(localHistory) : [];

      // Vorherige Prompts aus Datenbank laden
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getPromptHistory`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const dbPrompts = data.success ? (data.prompts || []) : [];
        
        // Kombiniere lokale und DB-Historie, entferne Duplikate
        const combinedPrompts = [...new Set([...localPrompts, ...dbPrompts])];
        
        // Sortiere nach Häufigkeit/Aktualität und begrenze auf 20
        const sortedPrompts = combinedPrompts
          .filter(prompt => prompt && prompt.trim().length > 0)
          .slice(0, 20);
          
        setPromptHistory(sortedPrompts);
        
        console.log('Loaded prompt history:', sortedPrompts);
      } else {
        // Fallback: nur lokale Historie
        setPromptHistory(localPrompts.slice(0, 20));
      }
    } catch (error) {
      console.error('Error loading prompt history:', error);
      // Fallback: nur lokale Historie
      const localHistory = localStorage.getItem('ai_prompt_history');
      const localPrompts = localHistory ? JSON.parse(localHistory) : [];
      setPromptHistory(localPrompts.slice(0, 20));
    }
  };

  // Prompt zur Historie hinzufügen
  const addToPromptHistory = (prompt: string) => {
    if (!prompt || prompt.trim().length === 0) return;
    
    const trimmedPrompt = prompt.trim();
    
    // Aktualisiere lokale Historie
    const currentHistory = promptHistory.filter(p => p !== trimmedPrompt);
    const newHistory = [trimmedPrompt, ...currentHistory].slice(0, 20);
    
    setPromptHistory(newHistory);
    
    // Speichere in localStorage
    localStorage.setItem('ai_prompt_history', JSON.stringify(newHistory));
    
    console.log('Added prompt to history:', trimmedPrompt);
  };

  // Prompt aus Historie entfernen
  const removeFromPromptHistory = (promptToRemove: string) => {
    const newHistory = promptHistory.filter(p => p !== promptToRemove);
    setPromptHistory(newHistory);
    localStorage.setItem('ai_prompt_history', JSON.stringify(newHistory));
    console.log('Removed prompt from history:', promptToRemove);
  };

  // Komplette Historie löschen
  const clearPromptHistory = () => {
    setPromptHistory([]);
    localStorage.removeItem('ai_prompt_history');
    console.log('Cleared complete prompt history');
    message.success('Prompt-Historie gelöscht');
  };

  // Media-Liste und aktive Generierungen laden
  const loadMediaFiles = async () => {
    console.log('=== loadMediaFiles called ===');
    setLoading(true);
    try {
      // Parallele Anfragen für normale Medien und aktive Generierungen
      const [mediaResponse, activeGenerationsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=listUserMedia`, {
          method: 'GET',
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getActiveGenerations`, {
          method: 'GET',
          credentials: 'include',
        })
      ]);
      
      let mediaFiles: MediaFile[] = [];
      let generatingFiles: MediaFile[] = [];

      // Normale Medien verarbeiten
      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        mediaFiles = mediaData.files || [];
      } else {
        console.error('Fehler beim Laden der Medien:', mediaResponse.status);
      }

      // Aktive Generierungen verarbeiten
      console.log('Active generations API response status:', activeGenerationsResponse.status);
      
      if (activeGenerationsResponse.ok) {
        const activeData = await activeGenerationsResponse.json();
        console.log('Active generations API data:', activeData);
        
        if (activeData.success && activeData.generations && activeData.generations.length > 0) {
          console.log('Restoring active generations:', activeData.generations);
          
          generatingFiles = activeData.generations.map((gen: any) => ({
            uid: gen.generationId,
            name: `AI: ${gen.prompt.substring(0, 30)}${gen.prompt.length > 30 ? '...' : ''}`,
            size: 0,
            uploadDate: new Date(gen.created * 1000).toLocaleString(),
            url: '',
            type: 'image/generating',
            isGenerating: true,
            generationId: gen.generationId,
            prompt: gen.prompt,
            promptId: gen.promptId,
            progress: gen.progress
          }));

          console.log('Generated files to restore:', generatingFiles);

          // Polling für alle aktiven Generierungen starten
          generatingFiles.forEach(file => {
            if (file.generationId) {
              console.log('Starting polling for generation:', file.generationId);
              startStatusPolling(file.generationId);
            }
          });

          setGeneratingCount(generatingFiles.length);
          console.log(`Restored ${generatingFiles.length} active generations`);
        } else {
          console.log('No active generations found or API response indicates no success:', {
            success: activeData.success,
            generations_length: activeData.generations ? activeData.generations.length : 'undefined',
            full_response: activeData
          });
        }
      } else {
        console.warn('Fehler beim Laden aktiver Generierungen:', activeGenerationsResponse.status);
        const errorText = await activeGenerationsResponse.text();
        console.warn('Active generations error response:', errorText);
      }

      // Kombiniere normale Medien und aktive Generierungen
      const allFiles = [...generatingFiles, ...mediaFiles];
      console.log('Setting combined files:', {
        generatingFiles_count: generatingFiles.length,
        mediaFiles_count: mediaFiles.length,
        allFiles_count: allFiles.length,
        allFiles: allFiles
      });
      
      setFiles(allFiles);
      setUploadCount(allFiles.length);

      if (!mediaResponse.ok && !activeGenerationsResponse.ok) {
        message.error('Fehler beim Laden der Medien');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Medien:', error);
      message.error('Fehler beim Laden der Medien');
    } finally {
      setLoading(false);
    }
  };

  // Datei löschen
  const deleteFile = async (fileUid: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=deleteMedia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ fileUid }),
      });

      if (response.ok) {
        setFiles(files.filter(file => file.uid !== fileUid));
        setUploadCount(uploadCount - 1);
        message.success('Datei erfolgreich gelöscht');
      } else {
        message.error('Fehler beim Löschen der Datei');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      message.error('Fehler beim Löschen der Datei');
    }
  };

  // AI Bildgenerierung starten
  const generateImage = async () => {
    if (!aiPrompt.trim()) {
      message.error('Bitte geben Sie einen Prompt ein');
      return;
    }

    if (uploadCount >= 100) {
      message.error('Maximale Anzahl von 100 Bildern erreicht');
      return;
    }

    // Prüfe ob erforderliche Bilder für spezielle Workflows vorhanden sind
    if (selectedWorkflow === 'CarBoatStaigingV1') {
      if (!carImage || !backgroundImage) {
        message.error('Bitte laden Sie sowohl ein Auto/Boot-Bild als auch ein Hintergrund-Bild hoch');
        return;
      }
    } else if (selectedWorkflow === 'FashionTony(API)') {
      if (!clothingImage || !modelImage) {
        message.error('Bitte laden Sie sowohl ein Kleidungsstück als auch ein Model-Bild hoch');
        return;
      }
    } else if (selectedWorkflow === 'BoatAndCarv2(api)') {
      if (!roomObjectImage || !styleImage) {
        message.error('Bitte laden Sie sowohl ein Raum/Objekt-Bild als auch ein Style-Bild hoch');
        return;
      }
    } else if (selectedWorkflow === 'VideoWithMotion(API)') {
      if (!videoFile || !referenceImage) {
        message.error('Bitte laden Sie sowohl ein Video als auch ein Referenzbild hoch');
        return;
      }
    }

    try {
      const requestData: any = {
        prompt: aiPrompt.trim(),
        comfyui_server: 'http://comfyui.sytes.net:8080',
        workflow: selectedWorkflow
      };
      
      // Füge zusätzliche Bilder hinzu je nach Workflow
      if (selectedWorkflow === 'CarBoatStaigingV1') {
        requestData.carImageData = carImage;
        requestData.backgroundImageData = backgroundImage;
      } else if (selectedWorkflow === 'FashionTony(API)') {
        requestData.clothingImageData = clothingImage;
        requestData.modelImageData = modelImage;
      } else if (selectedWorkflow === 'BoatAndCarv2(api)') {
        requestData.roomObjectImageData = roomObjectImage;
        requestData.styleImageData = styleImage;
      } else if (selectedWorkflow === 'VideoWithMotion(API)') {
        requestData.videoFileData = videoFile;
        requestData.referenceImageData = referenceImage;
      }

      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=generateImage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('AI Generation Started:', result); // Debug-Log
          
          // Temporäres "generating" File zur Liste hinzufügen
          const generatingFile: MediaFile = {
            uid: result.generationId,
            name: `AI: ${aiPrompt.substring(0, 30)}${aiPrompt.length > 30 ? '...' : ''}`,
            size: 0,
            uploadDate: new Date().toLocaleString(),
            url: '',
            type: 'image/generating',
            isGenerating: true,
            generationId: result.generationId,
            prompt: aiPrompt,
            promptId: result.promptId, // Für Debugging
            progress: {
              current_step: 'Workflow wird vorbereitet...',
              progress_percentage: 10,
              current_step_number: 1,
              total_steps: 5
            }
          };

          setFiles(prev => [generatingFile, ...prev]);
          setGeneratingCount(prev => prev + 1);
          setUploadCount(prev => prev + 1);
          
          // Status-Polling starten
          startStatusPolling(result.generationId);
          
          // Prompt zur Historie hinzufügen
          addToPromptHistory(aiPrompt);
          
          message.success(`Bildgenerierung gestartet! Prompt-ID: ${result.promptId}`);
          setAiPrompt(''); // Prompt-Feld leeren
        } else {
          message.error(result.error || 'Fehler beim Starten der Bildgenerierung');
        }
      } else {
        const errorText = await response.text();
        console.error('HTTP Error:', response.status, errorText);
        message.error(`Server-Fehler (${response.status}). Bitte ComfyUI-Verbindung testen.`);
      }
    } catch (error) {
      console.error('Fehler bei der Bildgenerierung:', error);
      message.error('Netzwerk-Fehler beim Starten der Bildgenerierung. Bitte Verbindung prüfen.');
    }
  };

  // Status-Polling für Bildgenerierung
  const startStatusPolling = (generationId: string) => {
    console.log('Starting status polling for generation:', generationId);
    
    // Prüfen ob bereits ein Interval für diese Generation läuft
    if (generationQueueRef.current.has(generationId)) {
      console.log('Polling already running for generation:', generationId);
      return;
    }
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=checkGenerationStatus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ generationId }),
        });

        if (response.ok) {
          const result = await response.json();
          
          console.log('Status-Check Result for', generationId, ':', result);
          
          if (result.status === 'completed' && result.imageUrl) {
            console.log('Generation completed, updating file:', {
              generationId,
              imageUrl: result.imageUrl,
              fileName: result.fileName,
              fileUid: result.fileUid,
              fileSize: result.fileSize
            });
            
            // Generierung abgeschlossen - Update das File mit funktionalem State Update
            setFiles(prevFiles => {
              const updatedFiles = prevFiles.map(file => {
                if (file.generationId === generationId) {
                  console.log('Updating file from:', file, 'to result:', result);
                  return {
                    ...file,
                    isGenerating: false,
                    url: result.imageUrl,
                    type: result.fileName?.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg',
                    size: result.fileSize || 0,
                    uid: result.fileUid ? String(result.fileUid) : file.uid,
                    name: result.fileName || file.name,
                    uploadDate: new Date().toLocaleString()
                  };
                }
                return file;
              });
              
              console.log('Files updated - found generated file:', 
                updatedFiles.find(f => f.generationId === generationId));
              
              return updatedFiles;
            });
            
            setGeneratingCount(prev => prev - 1);
            message.success(`Bild erfolgreich generiert: ${result.fileName}`);
            
            // Polling stoppen
            console.log('Clearing interval for completed generation:', generationId);
            clearInterval(pollInterval);
            generationQueueRef.current.delete(generationId);
            console.log('Remaining active polling intervals:', generationQueueRef.current.size);
            
            // Nach kurzer Pause Liste neu laden um sicherzugehen
            setTimeout(() => {
              console.log('Reloading media list to ensure sync...');
              loadMediaFiles();
            }, 1000);
            
          } else if (result.status === 'failed') {
            // Generierung fehlgeschlagen
            console.error('Generation Failed for', generationId, ':', result);
            
            setFiles(prev => {
              const filtered = prev.filter(file => file.generationId !== generationId);
              console.log('Removing failed generation, files count:', prev.length, '->', filtered.length);
              return filtered;
            });
            
            setGeneratingCount(prev => prev - 1);
            setUploadCount(prev => prev - 1);
            message.error(`Bildgenerierung fehlgeschlagen: ${result.error || 'Unbekannter Fehler'}`);
            
            // Polling stoppen
            console.log('Clearing interval for failed generation:', generationId);
            clearInterval(pollInterval);
            generationQueueRef.current.delete(generationId);
          } else if (result.status === 'cancelled') {
            // Generierung abgebrochen
            console.log('Generation Cancelled for', generationId, ':', result);
            
            setFiles(prev => {
              const filtered = prev.filter(file => file.generationId !== generationId);
              console.log('Removing cancelled generation, files count:', prev.length, '->', filtered.length);
              return filtered;
            });
            
            setGeneratingCount(prev => prev - 1);
            setUploadCount(prev => prev - 1);
            message.info('Bildgenerierung wurde abgebrochen');
            
            // Polling stoppen
            console.log('Clearing interval for cancelled generation:', generationId);
            clearInterval(pollInterval);
            generationQueueRef.current.delete(generationId);
          } else {
            // Status 'processing' oder andere - Progress updaten
            console.log('Generation', generationId, 'still processing, status:', result.status, 'progress:', result.progress);
            
            if (result.progress) {
              setFiles(prevFiles => {
                return prevFiles.map(file => {
                  if (file.generationId === generationId) {
                    return {
                      ...file,
                      progress: result.progress
                    };
                  }
                  return file;
                });
              });
            }
          }
        } else {
          const errorText = await response.text();
          console.error('Status-Check HTTP Error for', generationId, ':', response.status, errorText);
          
          // Bei wiederholten HTTP-Fehlern Polling stoppen
          if (response.status >= 400) {
            console.log('Stopping polling due to HTTP error for generation:', generationId);
            clearInterval(pollInterval);
            generationQueueRef.current.delete(generationId);
            
            setFiles(prev => prev.filter(file => file.generationId !== generationId));
            setGeneratingCount(prev => prev - 1);
            setUploadCount(prev => prev - 1);
            message.error('Fehler beim Status-Check der Bildgenerierung');
          }
        }
      } catch (error) {
        console.error('Fehler beim Status-Check für', generationId, ':', error);
      }
    }, 3000); // Alle 3 Sekunden prüfen

    // Polling-Interval speichern
    generationQueueRef.current.set(generationId, pollInterval);
    console.log('Active polling intervals:', generationQueueRef.current.size, 'Added interval for:', generationId);

    // Timeout nach 10 Minuten
    setTimeout(() => {
      if (generationQueueRef.current.has(generationId)) {
        console.log('Generation timeout for:', generationId);
        clearInterval(pollInterval);
        generationQueueRef.current.delete(generationId);
        
        // Timeout-File entfernen
        setFiles(prev => {
          const filtered = prev.filter(file => file.generationId !== generationId);
          console.log('Removing timed out generation, files count:', prev.length, '->', filtered.length);
          return filtered;
        });
        
        setGeneratingCount(prev => prev - 1);
        setUploadCount(prev => prev - 1);
        message.warning('Bildgenerierung abgebrochen (Timeout nach 10 Minuten)');
      }
    }, 600000); // 10 Minuten
  };

  // ComfyUI Verbindung testen
  const testComfyUIConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=testComfyUI`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();
      
      if (result.success) {
        message.success(`ComfyUI Server erreichbar! ${result.details?.system_stats ? 'System läuft normal.' : ''}`);
        console.log('ComfyUI Test Result:', result);
      } else {
        message.error(`ComfyUI Server nicht erreichbar: ${result.error}`);
        console.error('ComfyUI Test Error:', result);
      }
    } catch (error) {
      console.error('ComfyUI Test failed:', error);
      message.error('ComfyUI Test fehlgeschlagen - Überprüfen Sie die Netzwerkverbindung');
    } finally {
      setTestingConnection(false);
    }
  };

  // ComfyUI Modelle abfragen
  const getComfyUIModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getComfyUIModels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('ComfyUI Verfügbare Modelle:', result.models);
        message.info(`${result.count} Modelle verfügbar. Details in der Console.`);
      } else {
        message.error(`Fehler beim Laden der Modelle: ${result.error}`);
        console.error('ComfyUI Models Error:', result);
      }
    } catch (error) {
      console.error('ComfyUI Models failed:', error);
      message.error('Fehler beim Laden der verfügbaren Modelle');
    }
  };

  // ComfyUI Workflow testen
  const testComfyUIWorkflow = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=testComfyUIWorkflow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('ComfyUI Workflow Test erfolgreich:', result);
        message.success(`Workflow funktioniert! Modell: ${result.used_model} | Prompt ID: ${result.prompt_id}`);
        
        // Biete an, History für diese Prompt-ID zu debuggen
        if (result.prompt_id) {
          setTimeout(() => {
            message.info(`Debug verfügbar für Prompt-ID: ${result.prompt_id}`);
          }, 2000);
        }
      } else {
        message.error(`Workflow-Test fehlgeschlagen: ${result.error}`);
        console.error('ComfyUI Workflow Error:', result);
      }
    } catch (error) {
      console.error('ComfyUI Workflow test failed:', error);
      message.error('Fehler beim Workflow-Test');
    }
  };

  // ComfyUI History für Prompt-ID debuggen
  const debugComfyUIHistory = async (promptId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=debugComfyUIHistory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ promptId }),
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('ComfyUI History Debug:', result);
        message.success(`History abgerufen für Prompt-ID: ${promptId}. Details in der Console.`);
      } else {
        message.error(`History-Debug fehlgeschlagen: ${result.error}`);
        console.error('ComfyUI History Debug Error:', result);
      }
    } catch (error) {
      console.error('ComfyUI History debug failed:', error);
      message.error('Fehler beim History-Debug');
    }
  };

  // URL in Zwischenablage kopieren
  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      message.success('URL in Zwischenablage kopiert');
    });
  };

  // Upload-Eigenschaften
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    action: `${API_BASE_URL}?tx_buchungsmanager_frontend[action]=uploadMedia`,
    accept: '.jpg,.jpeg,.png,.gif,.webp,.pdf',
    withCredentials: true,
    showUploadList: false,
    beforeUpload: (file) => {
      // Prüfe Upload-Limit
      if (uploadCount >= maxFiles) {
        message.error('Upload-Limit erreicht. Bitte kontaktieren Sie den Support für Erweiterung.');
        return false;
      }

      // Prüfe Dateigröße (max. 10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Datei muss kleiner als 10MB sein!');
        return false;
      }

      // Prüfe Dateityp - Bilder und PDFs erlaubt
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      if (!isImage && !isPdf) {
        message.error('Nur Bilddateien (JPG, PNG, GIF, WebP) und PDF-Dateien sind erlaubt!');
        return false;
      }

      return true;
    },
    onChange: (info) => {
      const { status } = info.file;
      if (status === 'uploading') {
        setUploading(true);
      } else if (status === 'done') {
        setUploading(false);
        message.success(`${info.file.name} erfolgreich hochgeladen`);
        loadMediaFiles(); // Medien-Liste neu laden
      } else if (status === 'error') {
        setUploading(false);
        message.error(`${info.file.name} Upload fehlgeschlagen`);
      }
    },
  };

  // Gefilterte Dateien basierend auf Suchterm
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Dateigröße formatieren
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Komponente initialisieren
  useEffect(() => {
    loadMediaFiles();
    loadPromptHistory();
  }, []);

  // Cleanup: Stoppe alle laufenden Generierungen beim Verlassen der Komponente
  useEffect(() => {
    return () => {
      // Alle Polling-Intervalle stoppen
      generationQueueRef.current.forEach((interval) => {
        clearInterval(interval);
      });
      generationQueueRef.current.clear();
    };
  }, []);

  console.log('=== MediaManager render ===', {
    files_count: files.length,
    generatingCount: generatingCount,
    files_with_isGenerating: files.filter(f => f.isGenerating).length,
    files: files.map(f => ({ name: f.name, isGenerating: f.isGenerating, generationId: f.generationId }))
  });

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Medien & Bilder</Title>
        <Text type="secondary">
          Zentrale Verwaltung Ihrer Medien-Dateien. 
          {uploadCount}/{maxFiles} Dateien verwendet.
        </Text>
      </div>

      {/* Upload-Bereich */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={4}>Neue Medien hochladen</Title>
            {uploadCount >= maxFiles ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="warning" style={{ fontSize: '16px' }}>
                  ⚠️ Upload-Limit erreicht. Bitte kontaktieren Sie den Support für Erweiterung.
                </Text>
              </div>
            ) : (
              <Dragger {...uploadProps} disabled={uploading}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Klicken oder Dateien hierher ziehen zum Hochladen
                </p>
                <p className="ant-upload-hint">
                  Unterstützt: JPG, PNG, GIF, WebP, PDF (max. 10MB)
                </p>
                {uploading && <Progress percent={50} status="active" />}
              </Dragger>
            )}
          </Col>
        </Row>
      </Card>

      {/* AI Bildgenerierung */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={4}>
              <RobotOutlined style={{ marginRight: '8px' }} />
              KI-Bildgenerierung
            </Title>
          </Col>
          
          {/* Workflow Auswahl */}
          <Col span={24}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>Workflow auswählen:</Text>
              <Select
                value={selectedWorkflow}
                onChange={(value) => {
                  setSelectedWorkflow(value);
                  // Reset Bilder bei Workflow-Wechsel
                  setCarImage(null);
                  setBackgroundImage(null);
                  setClothingImage(null);
                  setModelImage(null);
                  setRoomObjectImage(null);
                  setStyleImage(null);
                  setVideoFile(null);
                  setReferenceImage(null);
                }}
                style={{ width: '100%' }}
                options={[
                  { value: 'standard', label: '🎨 Standard - Nur Prompt', icon: <PictureOutlined /> },
                  { value: 'CarBoatStaigingV1', label: '🚗 Auto/Boot Staging V1', icon: <CarOutlined /> },
                  { value: 'BoatAndCarv2(api)', label: '🚢 Boot/Auto Staging V2', icon: <CarOutlined /> },
                  { value: 'FashionTony(API)', label: '👔 Fashion/Mode', icon: <SkinOutlined /> },
                  { value: 'VideoWithMotion(API)', label: '🎬 Video mit Bewegung', icon: <VideoCameraOutlined /> },
                ]}
              />
            </Space>
          </Col>
          
          {/* Zusätzliche Upload-Felder für spezielle Workflows */}
          {selectedWorkflow === 'CarBoatStaigingV1' && (
            <>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setCarImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {carImage ? (
                    <img src={carImage} alt="Car" style={{ maxWidth: '100%', maxHeight: '120px' }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <CarOutlined />
                      </p>
                      <p className="ant-upload-text">Auto/Boot-Bild hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setBackgroundImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {backgroundImage ? (
                    <img src={backgroundImage} alt="Background" style={{ maxWidth: '100%', maxHeight: '120px' }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <PictureOutlined />
                      </p>
                      <p className="ant-upload-text">Hintergrund-Bild hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
            </>
          )}
          
          {selectedWorkflow === 'FashionTony(API)' && (
            <>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setClothingImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {clothingImage ? (
                    <img src={clothingImage} alt="Clothing" style={{ maxWidth: '100%', maxHeight: '120px' }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <SkinOutlined />
                      </p>
                      <p className="ant-upload-text">Kleidungsstück hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setModelImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {modelImage ? (
                    <img src={modelImage} alt="Model" style={{ maxWidth: '100%', maxHeight: '120px' }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <UserOutlined />
                      </p>
                      <p className="ant-upload-text">Model-Bild hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
            </>
          )}
          
          {/* BoatAndCarv2 UI */}
          {selectedWorkflow === 'BoatAndCarv2(api)' && (
            <>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setRoomObjectImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {roomObjectImage ? (
                    <img src={roomObjectImage} style={{ maxWidth: '100%', maxHeight: 200 }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Raum/Objekt-Bild hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setStyleImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {styleImage ? (
                    <img src={styleImage} style={{ maxWidth: '100%', maxHeight: 200 }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <PictureOutlined />
                      </p>
                      <p className="ant-upload-text">Style-Referenz hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
            </>
          )}
          
          {/* VideoWithMotion UI */}
          {selectedWorkflow === 'VideoWithMotion(API)' && (
            <>
              <Col span={12}>
                <Upload.Dragger
                  accept="video/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setVideoFile(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {videoFile ? (
                    <div style={{ textAlign: 'center' }}>
                      <VideoCameraOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                      <p>Video ausgewählt</p>
                    </div>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <VideoCameraOutlined />
                      </p>
                      <p className="ant-upload-text">Video hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
              <Col span={12}>
                <Upload.Dragger
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setReferenceImage(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  {referenceImage ? (
                    <img src={referenceImage} style={{ maxWidth: '100%', maxHeight: 200 }} />
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <PictureOutlined />
                      </p>
                      <p className="ant-upload-text">Referenzbild hochladen</p>
                    </>
                  )}
                </Upload.Dragger>
              </Col>
            </>
          )}
          
          <Col span={24}>
            <Space.Compact style={{ width: '100%' }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <Input
                  placeholder="Beschreiben Sie das gewünschte Bild (z.B. 'Ein schönes Ferienhaus am See bei Sonnenuntergang')"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onPressEnter={generateImage}
                  onFocus={() => promptHistory.length > 0 && setShowHistoryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowHistoryDropdown(false), 200)}
                  disabled={uploadCount >= maxFiles || generatingCount >= 3}
                  suffix={
                    <Space size="small">
                      {promptHistory.length > 0 && (
                        <Space size="small">
                          <Tooltip title="Prompt-Historie anzeigen">
                            <Badge count={promptHistory.length} size="small" offset={[-2, 2]}>
                              <SearchOutlined 
                                style={{ cursor: 'pointer', color: '#8c8c8c' }}
                                onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                              />
                            </Badge>
                          </Tooltip>
                          <Tooltip title="Prompt-Historie löschen">
                            <DeleteOutlined 
                              style={{ cursor: 'pointer', color: '#ff4d4f' }}
                              onClick={() => {
                                Modal.confirm({
                                  title: 'Prompt-Historie löschen',
                                  content: 'Möchten Sie wirklich die komplette Prompt-Historie löschen?',
                                  okText: 'Ja, löschen',
                                  cancelText: 'Abbrechen',
                                  onOk: clearPromptHistory
                                });
                              }}
                            />
                          </Tooltip>
                        </Space>
                      )}
                      {generatingCount > 0 && (
                        <Tag icon={<ClockCircleOutlined />} color="processing">
                          {generatingCount} generiere{generatingCount > 1 ? 'n' : 't'}
                        </Tag>
                      )}
                    </Space>
                  }
                />
                
                {/* Prompt-Historie Dropdown */}
                {showHistoryDropdown && promptHistory.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                      <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Prompt-Historie ({promptHistory.length})
                      </Text>
                    </div>
                    {promptHistory.map((historyPrompt, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          borderBottom: index < promptHistory.length - 1 ? '1px solid #f0f0f0' : 'none',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => {
                            setAiPrompt(historyPrompt);
                            setShowHistoryDropdown(false);
                          }}
                        >
                          <Text style={{ fontSize: '14px' }}>
                            {historyPrompt.length > 80 ? `${historyPrompt.substring(0, 80)}...` : historyPrompt}
                          </Text>
                        </div>
                        <Tooltip title="Diesen Prompt aus Historie entfernen">
                          <DeleteOutlined
                            style={{ 
                              color: '#ff4d4f', 
                              cursor: 'pointer', 
                              fontSize: '12px',
                              marginLeft: '8px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromPromptHistory(historyPrompt);
                            }}
                          />
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                type="primary" 
                icon={<RobotOutlined />}
                onClick={generateImage}
                disabled={!aiPrompt.trim() || uploadCount >= maxFiles || generatingCount >= 3}
                loading={generatingCount > 0}
              >
                Generieren
              </Button>
              {generatingCount > 0 && (
                <Button 
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Alle Generierungen abbrechen',
                      content: `Möchten Sie wirklich alle ${generatingCount} laufenden Bildgenerierungen abbrechen?`,
                      okText: 'Ja, alle abbrechen',
                      cancelText: 'Nein',
                      onOk: async () => {
                        console.log('Cancelling all generations...');
                        
                        // Alle generierenden Files finden
                        const generatingFiles = files.filter(file => file.isGenerating && file.generationId);
                        
                        // Alle Generierungen parallel abbrechen
                        const cancelPromises = generatingFiles.map(async (file) => {
                          try {
                            const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=cancelGeneration`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              credentials: 'include',
                              body: JSON.stringify({ generationId: file.generationId }),
                            });

                            if (response.ok) {
                              console.log('Cancelled generation:', file.generationId);
                              
                              // Polling stoppen
                              const interval = generationQueueRef.current.get(file.generationId!);
                              if (interval) {
                                clearInterval(interval);
                                generationQueueRef.current.delete(file.generationId!);
                              }
                            } else {
                              console.error('Failed to cancel generation:', file.generationId);
                            }
                          } catch (error) {
                            console.error('Error cancelling generation:', file.generationId, error);
                          }
                        });

                        await Promise.all(cancelPromises);

                        // Alle generierenden Files aus der Liste entfernen
                        setFiles(prev => prev.filter(file => !file.isGenerating));
                        setGeneratingCount(0);
                        setUploadCount(prev => prev - generatingFiles.length);
                        
                        message.success(`${generatingFiles.length} Generierung(en) abgebrochen`);
                      }
                    });
                  }}
                  size="middle"
                >
                  Alle abbrechen
                </Button>
              )}
              {generatingCount > 0 && (
                <Button 
                  danger
                  type="dashed"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: '⚠️ ALLE Generierungen LÖSCHEN',
                      content: (
                        <div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Diese Aktion löscht alle {generatingCount} laufenden Generierungen komplett!</strong>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Dies ist eine Notfall-Funktion für festhängende Generierungen.
                            Normalerweise sollten Sie "Alle abbrechen" verwenden.
                          </div>
                        </div>
                      ),
                      okText: 'Ja, ALLE LÖSCHEN',
                      cancelText: 'Abbrechen',
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        console.log('Deleting all generations...');
                        
                        // Alle generierenden Files finden
                        const generatingFiles = files.filter(file => file.isGenerating && file.generationId);
                        
                        // Alle Generierungen parallel löschen
                        const deletePromises = generatingFiles.map(async (file) => {
                          try {
                            const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=deleteGeneration`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              credentials: 'include',
                              body: JSON.stringify({ generationId: file.generationId }),
                            });

                            if (response.ok) {
                              console.log('Deleted generation:', file.generationId);
                              
                              // Polling stoppen
                              const interval = generationQueueRef.current.get(file.generationId!);
                              if (interval) {
                                clearInterval(interval);
                                generationQueueRef.current.delete(file.generationId!);
                              }
                            } else {
                              console.error('Failed to delete generation:', file.generationId);
                            }
                          } catch (error) {
                            console.error('Error deleting generation:', file.generationId, error);
                          }
                        });

                        await Promise.all(deletePromises);

                        // Alle generierenden Files aus der Liste entfernen
                        setFiles(prev => prev.filter(file => !file.isGenerating));
                        setGeneratingCount(0);
                        setUploadCount(prev => prev - generatingFiles.length);
                        
                        message.success(`${generatingFiles.length} festhängende Generierung(en) gelöscht`);
                      }
                    });
                  }}
                  size="middle"
                  style={{ marginLeft: '8px' }}
                >
                  Alle löschen
                </Button>
              )}
            </Space.Compact>
          </Col>
          {generatingCount >= 3 && (
            <Col span={24}>
              <Text type="warning" style={{ fontSize: '12px' }}>
                ⚠️ Maximal 3 Bilder können gleichzeitig generiert werden. Bitte warten Sie, bis eine Generierung abgeschlossen ist.
              </Text>
            </Col>
          )}
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 Tipp: Verwenden Sie detaillierte Beschreibungen für bessere Ergebnisse. 
                Die Generierung kann 1-3 Minuten dauern.
              </Text>
              <Space size="small">
                <Button 
                  size="small" 
                  type="link" 
                  onClick={getComfyUIModels}
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  Modelle
                </Button>
                <Button 
                  size="small" 
                  type="link" 
                  onClick={testComfyUIWorkflow}
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  Workflow
                </Button>
                <Button 
                  size="small" 
                  type="link" 
                  onClick={testComfyUIConnection}
                  loading={testingConnection}
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  {testingConnection ? 'Teste...' : 'Server'}
                </Button>
                <Button 
                  size="small" 
                  type="link" 
                  onClick={async () => {
                    try {
                      console.log('=== DEBUG: Testing APIs ===');
                      
                      const debugResponse = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=debugGenerations`, {
                        method: 'GET',
                        credentials: 'include',
                      });
                      
                      if (debugResponse.ok) {
                        const debugData = await debugResponse.json();
                        console.log('Debug Generations API:', debugData);
                        message.info(`Debug: ${debugData.total_generations} Generierungen gefunden`);
                      }
                      
                      const activeResponse = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getActiveGenerations`, {
                        method: 'GET',
                        credentials: 'include',
                      });
                      
                      if (activeResponse.ok) {
                        const activeData = await activeResponse.json();
                        console.log('Active Generations API:', activeData);
                        message.info(`Aktive: ${activeData.generations ? activeData.generations.length : 0} Generierungen`);
                      }
                      
                    } catch (error) {
                      console.error('Debug error:', error);
                      message.error('Debug-Fehler');
                    }
                  }}
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  Debug APIs
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Such- und Filterbereich */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={12}>
            <Search
              placeholder="Dateiname suchen..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space>
              <Tag color="blue">{filteredFiles.length} Dateien</Tag>
              <Button onClick={loadMediaFiles} loading={loading}>
                Aktualisieren
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Medien-Galerie */}
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Medien werden geladen...</div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <Empty
            description={
              searchTerm
                ? `Keine Medien gefunden für "${searchTerm}"`
                : 'Noch keine Medien vorhanden'
            }
          />
        ) : (
          <Row gutter={[16, 16]}>
            {filteredFiles.map((file) => (
              <Col key={file.uid} xs={24} sm={12} md={8} lg={6} xl={4}>
                                  <Card
                    hoverable={!file.isGenerating}
                    cover={
                      <div style={{ 
                        height: '160px', 
                        overflow: 'hidden', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        backgroundColor: file.isGenerating ? '#f0f0f0' : '#f5f5f5',
                        position: 'relative'
                      }}>
                        {file.isGenerating ? (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            color: '#1890ff',
                            padding: '8px'
                          }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '12px', fontSize: '12px', textAlign: 'center' }}>
                              <RobotOutlined style={{ marginRight: '4px' }} />
                              {file.progress?.current_step || 'KI generiert...'}
                            </div>
                            
                            {/* Progress Bar */}
                            <Progress 
                              percent={file.progress?.progress_percentage || 10}
                              size="small"
                              status="active"
                              strokeColor="#1890ff"
                              style={{ marginTop: '8px', width: '80%' }}
                            />
                            
                            {/* Step Counter */}
                            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                              Schritt {file.progress?.current_step_number || 1} von {file.progress?.total_steps || 5}
                              {file.progress?.queue_position && file.progress.queue_position > 0 && (
                                <span style={{ display: 'block', marginTop: '2px' }}>
                                  Warteschlange: Position {file.progress.queue_position}
                                </span>
                              )}
                            </div>
                            
                            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                              {file.prompt?.substring(0, 30)}{file.prompt && file.prompt.length > 30 ? '...' : ''}
                            </div>
                          </div>
                        ) : file.type?.startsWith('image/') ? (
                          <Image
                            src={file.url}
                            alt={file.name}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover' 
                            }}
                            preview={false}
                            onClick={() => {
                              setPreviewImage(file.url);
                              setPreviewTitle(file.name);
                              setPreviewVisible(true);
                            }}
                          />
                        ) : file.type === 'application/pdf' ? (
                          <div 
                            style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              cursor: 'pointer',
                              color: '#666'
                            }}
                            onClick={() => window.open(file.url, '_blank')}
                          >
                            <FileTextOutlined style={{ fontSize: '48px', marginBottom: '8px' }} />
                            <span style={{ fontSize: '12px' }}>PDF</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#666' }}>
                            <FileOutlined style={{ fontSize: '48px', marginBottom: '8px' }} />
                            <span style={{ fontSize: '12px' }}>Datei</span>
                          </div>
                        )}
                      </div>
                    }
                  actions={file.isGenerating ? [
                    <Tooltip title={`${file.progress?.current_step || 'Generierung läuft...'} (${file.progress?.progress_percentage || 10}%)`}>
                      {file.progress?.progress_percentage === 100 ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <ClockCircleOutlined style={{ color: '#1890ff' }} />
                      )}
                    </Tooltip>,
                    ...(file.promptId ? [
                      <Tooltip title="ComfyUI History debuggen">
                        <Button
                          type="text"
                          size="small"
                          onClick={() => debugComfyUIHistory(file.promptId!)}
                          style={{ color: '#722ed1' }}
                        >
                          Debug
                        </Button>
                      </Tooltip>
                    ] : []),
                    <Tooltip title="Generierung abbrechen">
                      <Popconfirm
                        title="Generierung abbrechen"
                        description="Möchten Sie die Bildgenerierung abbrechen?"
                        onConfirm={async () => {
                          console.log('Cancelling generation:', file.generationId);
                          
                          try {
                            // API-Call zum Server senden
                            const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=cancelGeneration`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              credentials: 'include',
                              body: JSON.stringify({ generationId: file.generationId }),
                            });

                            if (response.ok) {
                              const result = await response.json();
                              console.log('Generation cancelled on server:', result);
                              
                              // Polling stoppen für diese Generation
                              const interval = generationQueueRef.current.get(file.generationId!);
                              if (interval) {
                                clearInterval(interval);
                                generationQueueRef.current.delete(file.generationId!);
                                console.log('Cleared interval for generation:', file.generationId);
                              }
                              
                              // File aus Liste entfernen
                              setFiles(prev => {
                                const filtered = prev.filter(f => f.generationId !== file.generationId);
                                console.log('Removing cancelled generation, files count:', prev.length, '->', filtered.length);
                                return filtered;
                              });
                              
                              setGeneratingCount(prev => prev - 1);
                              setUploadCount(prev => prev - 1);
                              
                              message.success(
                                result.comfyui_cancelled 
                                  ? 'Generierung erfolgreich bei ComfyUI abgebrochen' 
                                  : 'Generierung lokal abgebrochen (ComfyUI bereits fertig oder nicht erreichbar)'
                              );
                            } else {
                              const errorText = await response.text();
                              console.error('Failed to cancel generation:', response.status, errorText);
                              message.error('Fehler beim Abbrechen der Generierung');
                            }
                          } catch (error) {
                            console.error('Error cancelling generation:', error);
                            message.error('Fehler beim Abbrechen der Generierung');
                          }
                        }}
                        okText="Ja"
                        cancelText="Nein"
                      >
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      </Popconfirm>
                    </Tooltip>,
                    <Tooltip title="Festhängende Generierung komplett löschen">
                      <Popconfirm
                        title="Generierung löschen"
                        description={
                          <div>
                            <div>⚠️ <strong>ACHTUNG:</strong> Diese Aktion löscht die Generierung komplett!</div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              Nur bei festhängenden Generierungen verwenden.
                            </div>
                          </div>
                        }
                        onConfirm={async () => {
                          console.log('Deleting generation:', file.generationId);
                          
                          try {
                            // API-Call zum Server senden
                            const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=deleteGeneration`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              credentials: 'include',
                              body: JSON.stringify({ generationId: file.generationId }),
                            });

                            if (response.ok) {
                              const result = await response.json();
                              console.log('Generation deleted on server:', result);
                              
                              // Polling stoppen für diese Generation
                              const interval = generationQueueRef.current.get(file.generationId!);
                              if (interval) {
                                clearInterval(interval);
                                generationQueueRef.current.delete(file.generationId!);
                                console.log('Cleared interval for generation:', file.generationId);
                              }
                              
                              // File aus Liste entfernen
                              setFiles(prev => {
                                const filtered = prev.filter(f => f.generationId !== file.generationId);
                                console.log('Removing deleted generation, files count:', prev.length, '->', filtered.length);
                                return filtered;
                              });
                              
                              setGeneratingCount(prev => prev - 1);
                              setUploadCount(prev => prev - 1);
                              
                              message.success('Festhängende Generierung wurde komplett gelöscht');
                            } else {
                              const errorText = await response.text();
                              console.error('Failed to delete generation:', response.status, errorText);
                              message.error('Fehler beim Löschen der Generierung');
                            }
                          } catch (error) {
                            console.error('Error deleting generation:', error);
                            message.error('Fehler beim Löschen der Generierung');
                          }
                        }}
                        okText="Ja, löschen"
                        cancelText="Abbrechen"
                        okButtonProps={{ danger: true }}
                      >
                        <DeleteOutlined style={{ color: '#d9534f' }} />
                      </Popconfirm>
                    </Tooltip>
                  ] : [
                    <Tooltip title={file.type === 'application/pdf' ? 'PDF öffnen' : 'Vorschau'}>
                      <EyeOutlined
                        onClick={() => {
                          if (file.type === 'application/pdf') {
                            window.open(file.url, '_blank');
                          } else {
                            setPreviewImage(file.url);
                            setPreviewTitle(file.name);
                            setPreviewVisible(true);
                          }
                        }}
                      />
                    </Tooltip>,
                    <Tooltip title="URL kopieren">
                      <CopyOutlined onClick={() => copyToClipboard(file.url)} />
                    </Tooltip>,
                    <Popconfirm
                      title="Datei löschen"
                      description="Sind Sie sicher, dass Sie diese Datei löschen möchten?"
                      onConfirm={() => deleteFile(file.uid)}
                      okText="Ja"
                      cancelText="Nein"
                    >
                      <Tooltip title="Löschen">
                        <DeleteOutlined style={{ color: '#ff4d4f' }} />
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                >
                  <Card.Meta
                    title={
                      <Tooltip title={file.name}>
                        <div style={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}>
                          {file.isGenerating && <RobotOutlined style={{ marginRight: '4px', color: '#1890ff' }} />}
                          {file.name}
                        </div>
                      </Tooltip>
                    }
                    description={
                      <div>
                        <div>
                          {file.isGenerating ? (
                            <div>
                              <Tag 
                                icon={file.progress?.progress_percentage === 100 ? <CheckCircleOutlined /> : <ClockCircleOutlined />} 
                                color={file.progress?.progress_percentage === 100 ? "success" : "processing"}
                                style={{ marginBottom: '4px' }}
                              >
                                {file.progress?.current_step || 'Wird generiert...'}
                              </Tag>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                {file.progress?.progress_percentage || 10}% - Schritt {file.progress?.current_step_number || 1}/{file.progress?.total_steps || 5}
                                {file.progress?.queue_position && file.progress.queue_position > 0 && (
                                  <span> • Warteschlange: {file.progress.queue_position}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            formatFileSize(file.size)
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {new Date(file.uploadDate).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Vorschau-Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
}; 